import {
  BookkeeperAccountVisibilityTypes,
  ExtendCurrency,
  ExtendLocales,
  MfaPreferences,
  OrganizationRoles,
} from 'src/enums'
import { SaasTierType } from 'src/models/organizations.models'
import { ScreeningStatus } from 'src/models/virtualCard.models'

export enum UserAvatarType {
  Upload = 'UPLOAD',
  Gravatar = 'GRAVATAR',
  Default = 'DEFAULT',
}

export enum UserAssociation {
  invitationTo = 'INVITATION_TO',
  invitationFrom = 'INVITATION_FROM',
  virtualCardFrom = 'VIRTUAL_CARD_FROM',
  virtualCardTo = 'VIRTUAL_CARD_TO',
  delegateCreditCardTo = 'DELEGATE_CREDIT_CARD_TO',
  delegateCreditCardFrom = 'DELEGATE_CREDIT_CARD_FROM',
}

export interface UserTokenResponse {
  firstName?: string
  lastName?: string
  email: string
  locale: string
}

export interface IssuerSanctionsResponse {
  name: string
  status: ScreeningStatus
  code: string
}

export interface UserOrganization {
  id: string
  industry: string
  useCase: string
  role: OrganizationRoles
  explicit: boolean
  joinedAt: string
}

export interface UserDepartment {
  id: string
  name: string
}

export interface UserStatistics {
  vcnsWithSpendCount: number
  activeVcnsWithSpendCount: number
  inactiveVcnsWithSpendCount: number
  pendingVcnsWithSpendCount: number
  activeBudgetsWithSendCount: number
  inactiveBudgetsWithSendCount: number
  inactiveAccountsWithRegistrarCount: number
  blockedAccountsWithRegistrarCount: number
  activeAccountsWithRegistrarCount: number
  pendingAccountsWithRegistrarCount: number
  budgetsWithSendCount: number
  accountsWithRegistrarCount: number
  hasBudgetsWithSend: boolean
  hasAccountsWithRegistrar: boolean
}

export interface SecondaryEmailResponse {
  email: string
  emailVerified: boolean
}

export interface UserListResponse {
  id: string
  userId?: string
  firstName?: string
  lastName?: string
  email: string
  avatarType?: UserAvatarType
  avatarUrl?: string
  createdAt: string
  updatedAt: string
  currency: ExtendCurrency
  locale: ExtendLocales
  timezone: string
  employeeId?: string
  issuerSanctions?: IssuerSanctionsResponse[]
  verified?: boolean
  phone?: string
  phoneVerified?: boolean
  organization: UserOrganization
  organizationId?: string
  organizationRole?: OrganizationRoles
  lastLoggedInAt?: string
  statistics?: UserStatistics
  mfaPreference?: MfaPreferences
  firstNameOrEmail: string
  lastNameOrEmail: string
  hasFullName: boolean
  fullNameOrEmail: string
  truncatedFullNameOrEmail: string
  initials: string
  cardImageFullName: string
  isInExplicitOrg: boolean
  hasOrgMemberPermissions: boolean
  hasOrgBookkeeperPermissions: boolean
  hasOrgAdminPermissions: boolean
  hasOrgOwnerPermissions: boolean
  orgRoleDisplayTitle: string
  orgRoleDisplayTagTitle: string
  accounts?: number
  budgets?: number
  virtualCards?: number
  deactivated?: boolean
  department?: UserDepartment
  smsEnabled?: boolean
}

export class UserListModel {
  public id: string
  public firstName?: string
  public lastName?: string
  public email: string
  public avatarType?: UserAvatarType
  public avatarUrl?: string
  public createdAt: string
  public updatedAt: string
  public currency: ExtendCurrency
  public locale: ExtendLocales
  public timezone: string
  public employeeId?: string
  public issuerSanctions?: IssuerSanctionsResponse[]
  public verified?: boolean
  public phone?: string
  public phoneVerified?: boolean
  public organization: UserOrganization
  public organizationId?: string
  public organizationRole?: OrganizationRoles
  public lastLoggedInAt?: string
  public statistics?: UserStatistics
  public mfaPreference?: MfaPreferences
  public accounts?: number
  public budgets?: number
  public virtualCards?: number
  public deactivated?: boolean
  public department?: UserDepartment
  public smsEnabled: boolean

  public constructor(data: UserListResponse) {
    this.id = data.id
    this.firstName = data.firstName
    this.lastName = data.lastName
    this.email = data.email
    this.avatarType = data.avatarType
    this.avatarUrl = data.avatarUrl
    this.createdAt = data.createdAt
    this.updatedAt = data.updatedAt
    this.currency = data.currency
    this.locale = data.locale
    this.timezone = data.timezone
    this.employeeId = data.employeeId
    this.issuerSanctions = data.issuerSanctions
    this.verified = data.verified
    this.phone = data.phone
    this.phoneVerified = data.phoneVerified
    this.organization = data.organization
    this.organizationId = data.organizationId
    this.organizationRole = data.organizationRole
    this.lastLoggedInAt = data.lastLoggedInAt
    this.statistics = data.statistics
    this.mfaPreference = data.mfaPreference
    this.accounts = data.accounts
    this.budgets = data.budgets
    this.virtualCards = data.virtualCards
    this.deactivated = data.deactivated
    this.department = data.department
    this.smsEnabled = data.smsEnabled ?? true
  }

  public get firstNameOrEmail(): string {
    return this.firstName !== undefined ? this.firstName : this.email
  }

  public get lastNameOrEmail(): string {
    return this.lastName !== undefined ? this.lastName : this.email
  }

  public get hasFullName(): boolean {
    return this.firstName !== undefined && this.lastName !== undefined
  }

  public get fullName(): string | undefined {
    return this.hasFullName ? `${this.firstName} ${this.lastName}` : undefined
  }

  public get fullNameOrEmail(): string {
    return this.fullName || this.email
  }

  public get truncatedFullNameOrEmail(): string {
    return this.fullNameOrEmail.length > 45
      ? `${this.fullNameOrEmail.slice(0, 44)}...`
      : this.fullNameOrEmail
  }

  public get initials(): string {
    if (this.firstName !== undefined && this.lastName !== undefined) {
      return `${this.firstName.slice(0, 1)}${this.lastName.slice(0, 1)}`.toUpperCase()
    } else {
      return this.email?.[0] ?? ''
    }
  }

  public get cardImageFullName(): string {
    return this.firstName && this.lastName
      ? this.firstName.length + this.lastName.length < 26
        ? `${this.firstName} ${this.lastName}`
        : `${this.firstName[0]} ${this.lastName.slice(0, 24)}`
      : this.email
  }

  public get isInExplicitOrg(): boolean {
    return this.organization !== undefined && this.organization.explicit
  }

  public get hasOrgMemberPermissions(): boolean {
    return (
      this.isInExplicitOrg &&
      (this.organizationRole === OrganizationRoles.member ||
        this.organizationRole === OrganizationRoles.bookkeeper ||
        this.organizationRole === OrganizationRoles.cardManager ||
        this.organizationRole === OrganizationRoles.admin ||
        this.organizationRole === OrganizationRoles.owner)
    )
  }

  public get hasOrgCardManagerPermissions(): boolean {
    return (
      this.isInExplicitOrg &&
      (this.organizationRole === OrganizationRoles.cardManager ||
        this.organizationRole === OrganizationRoles.bookkeeper ||
        this.organizationRole === OrganizationRoles.admin ||
        this.organizationRole === OrganizationRoles.owner)
    )
  }

  public get hasOrgBookkeeperPermissions(): boolean {
    return (
      this.isInExplicitOrg &&
      (this.organizationRole === OrganizationRoles.bookkeeper ||
        this.organizationRole === OrganizationRoles.admin ||
        this.organizationRole === OrganizationRoles.owner)
    )
  }

  public get hasOrgAdminPermissions(): boolean {
    return (
      this.isInExplicitOrg &&
      (this.organizationRole === OrganizationRoles.admin ||
        this.organizationRole === OrganizationRoles.owner)
    )
  }

  public get hasOrgOwnerPermissions(): boolean {
    return (
      this.isInExplicitOrg && this.organizationRole === OrganizationRoles.owner
    )
  }

  public get orgRoleDisplayTitle(): string {
    return this.hasOrgOwnerPermissions
      ? 'Org Owner'
      : this.hasOrgAdminPermissions
        ? 'Admin'
        : this.hasOrgBookkeeperPermissions
          ? 'Bookkeeper'
          : this.hasOrgCardManagerPermissions
            ? 'Card Manager'
            : this.hasOrgMemberPermissions
              ? 'Member'
              : this.organizationRole === OrganizationRoles.guest
                ? 'Guest'
                : ''
  }

  public get orgRoleDisplayTagTitle(): string {
    return this.hasOrgOwnerPermissions
      ? 'Owner'
      : this.hasOrgAdminPermissions
        ? 'Admin'
        : this.hasOrgBookkeeperPermissions
          ? 'Bookkeeper'
          : this.hasOrgCardManagerPermissions
            ? 'Card Manager'
            : this.hasOrgMemberPermissions
              ? 'Member'
              : this.organizationRole === OrganizationRoles.guest
                ? 'Guest'
                : ''
  }

  public get departmentMemberDisplayTagTitle(): string {
    return this.organizationRole === OrganizationRoles.owner
      ? 'Owner'
      : this.organizationRole === OrganizationRoles.admin
        ? 'Admin'
        : this.organizationRole === OrganizationRoles.bookkeeper
          ? 'Bookkeeper'
          : this.organizationRole === OrganizationRoles.cardManager
            ? 'Card Manager'
            : this.organizationRole === OrganizationRoles.member
              ? 'Member'
              : this.organizationRole === OrganizationRoles.guest
                ? 'Guest'
                : ''
  }
}

export interface OrganizationMembershipResponse {
  organizationMembership: {
    role: OrganizationRoles
    organizationId: string
    userId: string
    joinedAt: string
    accountVisibility?: {
      type: BookkeeperAccountVisibilityTypes
      ids: string[]
    }
    department?: UserDepartment
  }
}

export enum UserFeature {
  ORGANIZATION_USER_STATISTICS = 'ORGANIZATION_USER_STATISTICS',
  SEND_ORGANIZATION_RECEIPT_REMINDER = 'SEND_ORGANIZATION_RECEIPT_REMINDER',
  CREATE_BILL_PAY_CARD = 'CREATE_BILL_PAY_CARD',
  VIEW_BILL_PAY_SECTION = 'VIEW_BILL_PAY_SECTION',
  REGISTER_ACCOUNT = 'REGISTER_ACCOUNT',
  QUICKBOOKS_DOWNLOAD = 'QUICKBOOKS_DOWNLOAD',
  VIEW_AUTOMATION_RULES_SECTION = 'VIEW_AUTOMATION_RULES_SECTION',
  EDIT_EXPENSE_CATEGORIES = 'EDIT_EXPENSE_CATEGORIES',
  EDIT_ADDITIONAL_FIELDS = 'EDIT_ADDITIONAL_FIELDS',
  API_KEY_MANAGER = 'API_KEY_MANAGER',
  VIEW_AMEX_CPG_INTEGRATION = 'VIEW_AMEX_CPG_INTEGRATION',
  VIEW_VIRTUAL_CARD_FEATURES = 'VIEW_VIRTUAL_CARD_FEATURES',
  // Saas Entitlements
  SAAS_ENABLED = 'SAAS_ENABLED',
  API_KEY = 'API_KEY',
  GUEST_ROLE = 'GUEST_ROLE',
  ORGANIZATION_TOTAL_SIZE = 'ORGANIZATION_TOTAL_SIZE',
  VIRTUAL_CARD_LIMIT = 'VIRTUAL_CARD_LIMIT',
  EXPENSE_CATEGORY_LIMIT = 'EXPENSE_CATEGORY_LIMIT',
  EXPENSE_CATEGORY_RULES = 'EXPENSE_CATEGORY_RULES',
  VIEW_ADDITIONAL_FIELDS = 'VIEW_ADDITIONAL_FIELDS',
  SEND_TO_BANK = 'SEND_TO_BANK',
  AUTOMATED_EXPENSE_REPORTS = 'AUTOMATED_EXPENSE_REPORTS',
  BUSINESS_RULES = 'BUSINESS_RULES',
  QUICKBOOKS_INTEGRATION = 'QUICKBOOKS_INTEGRATION',
  XERO_INTEGRATION = 'XERO_INTEGRATION',
  SAGE_INTACCT_INTEGRATION = 'SAGE_INTACCT_INTEGRATION',
  NETSUITE_INTEGRATION = 'NETSUITE_INTEGRATION',
  DYNAMICS_BC_INTEGRATION = 'DYNAMICS_BC_INTEGRATION',
  MANAGER_ASSIGNMENT = 'MANAGER_ASSIGNMENT',
  AI_EXPENSE_CATEGORIES = 'AI_EXPENSE_CATEGORIES',
  AI_ASSISTANT = 'AI_ASSISTANT',
  SSO_ENABLED = 'SSO_ENABLED',
  // ---
}

// TODO: remove this mapping after backend gives us a better solution

export const userFeatureRequiredTier = (feature: UserFeature): SaasTierType => {
  switch (feature) {
    case UserFeature.SSO_ENABLED:
    case UserFeature.NETSUITE_INTEGRATION:
    case UserFeature.DYNAMICS_BC_INTEGRATION:
    case UserFeature.API_KEY:
      return 'ENTERPRISE'
    case UserFeature.GUEST_ROLE:
    case UserFeature.BUSINESS_RULES:
    case UserFeature.QUICKBOOKS_INTEGRATION:
    case UserFeature.XERO_INTEGRATION:
    case UserFeature.SAGE_INTACCT_INTEGRATION:
    case UserFeature.SEND_TO_BANK:
    case UserFeature.EXPENSE_CATEGORY_RULES:
    case UserFeature.AUTOMATED_EXPENSE_REPORTS:
    case UserFeature.MANAGER_ASSIGNMENT:
    case UserFeature.VIEW_ADDITIONAL_FIELDS:
      return 'PRO'
    default:
      return 'STARTER'
  }
}

export type UserFeatureList = Record<UserFeature, boolean | undefined>
